//shared containers
import { Footer } from '@/containers-m/Footer';
import { Navigation } from '/containers-m/';

//containers
import {
  Hero,
  WatchItRunYourFreight,
  HowItWorks,
  RunsOnYourStack,
  TheRestOfTheLineup,
  EndCTA,
} from '../containers';

//styles
import {
  PageWrapper,
  ProactiveLoadAuditsGlobalStyle,
} from './ProactiveLoadAudits.styles';

const hero = {
  slug: 'proactive-load-audits',
  title: 'Proactive Load Audits',
  description:
    'Checks every load before pickup, flags at-risk transit while it can still be fixed, and audits every charge against the contracted rate and approved accessorials — before payment, not after.',
  metric: '30–40%',
  metricLabel: 'reduction in avoidable penalty charges',
  metricSource: 'MEASURED AT WHIRLWIND',
};

const watchItRun = {
  title: 'A load’s at risk before pickup',
  stageHeadTitle:
    'Pickups start in 2 hours — and something’s off on three loads.',
  stageHeadMeta: 'Audit · AM pickups',
  rows: [
    {
      time: '05:58:00',
      text: 'Pre-pickup audit on 38 loads picking up this morning.',
    },
    {
      time: '05:58:20',
      text: '#LS-50733: tender says reefer, the order needs a dry van.',
    },
    {
      time: '05:58:22',
      text: 'Carrier confirmed the mix-up — corrected before dispatch.',
    },
    {
      time: '05:58:40',
      text: '2 more flagged: appointment missing, instructions stale.',
    },
    {
      time: '05:59:05',
      text: 'All 3 fixed before pickup. No day-of surprises.',
    },
  ],
  summaryText:
    'Before the trucks rolled, Loadsmart AI audited the morning’s loads and caught a wrong equipment type, a missing appointment, and stale instructions — fixing all three ahead of pickup so none turned into detention, a rejection, or a day-of fire drill.',
  agentBadge: 'Audit Agent',
  escalationTitle: 'Prevention, not reconciliation.',
  escalationText:
    'A recovered accessorial gives back part of a dollar you already spent. A prevented accessorial never leaves the account. At volume, the gap between auditing a sample after payment and auditing every load before pickup is the gap between a leak you monitor and a leak you close.',
};

const howItWorks = {
  stepsTitleWhite: 'Audits every load before pickup.',
  stepsTitleGradient: 'Not a sample after payment.',
  steps: [
    {
      title: 'Pre-pickup check on every load',
      description:
        'Validates the load against the rate, the routing guide, the appointment and the documents it needs to move clean — while mismatches are still fixable.',
    },
    {
      title: 'Early at-risk transit flags',
      description:
        'Raises a load drifting toward a chargeable event — a late pickup, a slipping window, a facility already running behind — before the detention clock starts.',
    },
    {
      title: 'Charges audited against the rules',
      description:
        'Every invoice checked against the contracted rate, the booked rate, approved accessorials and the shipment record, before approval.',
    },
    {
      title: 'Exceptions routed to a person',
      description:
        'Anything outside the guardrails you set escalates to a Loadsmart Freight Expert or back to your own team, whichever you prefer.',
    },
  ],
  outcomes: [
    {
      title: 'Finance reviews instead of reconstructing',
      description:
        'Exceptions arrive validated, rather than as a detention event to piece together from three weeks ago.',
    },
    {
      title: 'Detention becomes visible while it’s happening',
      description:
        'Live appointment and gate data show the wait as it runs, not on a bill weeks later.',
    },
    {
      title: 'No dispute to argue',
      description:
        'Prevention before pickup produces no incorrect charge to fight over in the first place.',
    },
  ],
};

const lineupCards = [
  {
    slug: 'document-collection-and-filing',
    title: 'Document Collection & Filing',
    description: 'Supplies the records every audit is checked against.',
  },
  {
    slug: 'claims-processing',
    title: 'Claims Processing',
    description:
      'Picks it up when the money has to be recovered rather than prevented.',
  },
  {
    slug: 'tracking-and-status-updates',
    title: 'Tracking & Status Updates',
    description: 'One of the six agents that make up Loadsmart AI.',
  },
  {
    slug: 'tender-failures',
    title: 'Tender Failures',
    description: 'One of the six agents that make up Loadsmart AI.',
  },
  {
    slug: 'scheduling-and-re-scheduling',
    title: 'Scheduling & Re-Scheduling',
    description: 'One of the six agents that make up Loadsmart AI.',
  },
];

function ProactiveLoadAudits() {
  return (
    <PageWrapper>
      <ProactiveLoadAuditsGlobalStyle />
      <Navigation theme="dark" />
      <Hero {...hero} />
      <WatchItRunYourFreight {...watchItRun} />
      <HowItWorks {...howItWorks} />
      <RunsOnYourStack />
      <TheRestOfTheLineup cards={lineupCards} />
      <EndCTA agentName="Proactive Load Audits" />
      <Footer />
    </PageWrapper>
  );
}

export default ProactiveLoadAudits;
export { ProactiveLoadAudits };
